/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */

// Material Dashboard 2 React components
import { useState } from "react"
import { Card, CircularProgress, Icon, Modal, Tooltip } from "@mui/material"
import PropTypes from "prop-types"
import moment from "moment/moment"
import { useNavigate } from "react-router-dom"
import { pdf } from "@react-pdf/renderer"

import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDBadge from "components/MDBadge"
import MDButton from "components/MDButton"
import MDSnackbar from "components/MDSnackbar"
import MDCheckbox from "components/MDCheckbox"
import translate from "utils/translate"
import { translateStatus } from "utils/translate/status"
import { dataURLtoBlob } from "utils/functions/global"

// Import services
import * as packageService from "services/packages/package.service"
import { printLazajellaPDF } from "services/packages/package.service"

// Document
import { PackageDocument } from "../pdf/PackageDocument"
import { printChahinPackages } from "layouts/packages/pdf/ChahinPackageDocument"
import { generateColorPerStatus } from "utils/functions/switchStatusColor"
import { PackageDocumentMylrez } from "../pdf/PackageDocumentMylrez"

const StateCity = ({ state, city }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography display="block" variant="p" color="text" fontWeight="medium">
      {state}
    </MDTypography>
    <MDTypography variant="caption">{city}</MDTypography>
  </MDBox>
)

const Action = ({ packageId, companyName, barcode, isPending, packageDetails }) => {
  const navigate = useNavigate()
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false)
  const [snackbarObject, setSnackbarObject] = useState({
    open: false,
    content: "",
    color: "primary",
    icon: "",
  })
  const [isLoadingPDF, setIsLoadingPDF] = useState(false)

  // Style the card modal
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    boxShadow: 24,
    p: 4,
  }

  // Handle open delete modal
  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpened(true)
  }

  // Handle close delete modal
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpened(false)
  }

  // Handle open snackbar
  const handleOpenSnackbar = (status, content, icon) => {
    setSnackbarObject({
      ...snackbarObject,
      open: true,
      content,
      color: status,
      icon,
    })
  }

  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setSnackbarObject({
      ...snackbarObject,
      open: false,
    })
  }

  // Handle update a package
  const updatePackage = () => {
    if (companyName === "lazajella") {
      navigate(`modifier-colis/${packageId}`)
    }
  }

  // Handle delete a package
  const removePackage = async () => {
    if (companyName === "lazajella") {
      try {
        const { status, data } = await packageService.deletePackage(packageId)
        if (status === 200) {
          handleCloseDeleteModal()
          navigate(0)
        } else {
          const message = data?.message || "Impossible de supprimer le colis"
          handleOpenSnackbar("error", message, "error")
        }
      } catch (error) {
        handleOpenSnackbar("error", translate.something_wrong, "error")
      }
    }
  }

  const isAlamanaCompany = () => {
    if (companyName === "alamana") return true
    return false
  }

  const isMylerzCompany = () => {
    if (companyName === "mylerz") return true
    return false
  }
  const isChahinCompany = () => {
    if (companyName === "chahin") return true
    return false
  }

  // Handle print alamana PDF package
  const printAlamanaPackage = async () => {
    setIsLoadingPDF(true)
    try {
      const { status, data } = await packageService.generateCodes(barcode)
      if (status === 200) {
        const pdfDocument = pdf(
          <PackageDocument
            dataDocument={packageDetails}
            generatedQRCode={data.qrcode}
            generatedBarcode={data.barcode}
          />
        ).toBlob()
        pdfDocument.then((blob) => {
          const url = URL.createObjectURL(blob)
          setIsLoadingPDF(false)
          window.open(url, "_blank")
        })
      }
    } catch (error) {
      setIsLoadingPDF(true)
      handleOpenSnackbar("error", translate.something_wrong, "error")
    }
  }

  // Handle print lazajella PDF package
  const printLazajellaPackage = async () => {
    setIsLoadingPDF(true)
    try {
      const result = await printLazajellaPDF(barcode)
      setIsLoadingPDF(false)
      const base64EncodedPDF = result.data
      const blob = dataURLtoBlob(base64EncodedPDF, "application/pdf")
      const url = URL.createObjectURL(blob)
      setIsLoadingPDF(false)
      window.open(url, "_blank")
    } catch (error) {
      setIsLoadingPDF(false)
      handleOpenSnackbar("error", translate.something_wrong, "error")
    }
  }

  // Handle print mylerz PDF package
  const printMylerzPackage = async () => {
    setIsLoadingPDF(true)
    try {
      const { status, data } = await packageService.generateCodes(barcode)
      if (status === 200) {
        const pdfDocument = pdf(
          <PackageDocumentMylrez
            dataDocument={packageDetails}
            generatedQRCode={data.qrcode}
            generatedBarcode={data.barcode}
          />
        ).toBlob()
        pdfDocument.then((blob) => {
          const url = URL.createObjectURL(blob)
          setIsLoadingPDF(false)
          window.open(url, "_blank")
        })
      }
    } catch (error) {
      setIsLoadingPDF(true)
      handleOpenSnackbar("error", translate.something_wrong, "error")
    }
  }

  // Handle print chahin PDF package (label generated in the browser)
  const printChahinPackage = async () => {
    setIsLoadingPDF(true)
    try {
      const { url, warnings } = await printChahinPackages([packageDetails])
      if (warnings.length > 0) handleOpenSnackbar("warning", warnings[0], "warning")
      window.open(url, "_blank")
    } catch (error) {
      handleOpenSnackbar("error", error.message || translate.something_wrong, "error")
    } finally {
      setIsLoadingPDF(false)
    }
  }

  const handlePrint = () => {
    if (companyName === "lazajella") {
      printLazajellaPackage()
    } else if (companyName === "alamana") {
      printAlamanaPackage()
    } else if (companyName === "chahin") {
      printChahinPackage()
    } else if (companyName === "mylerz") {
      printMylerzPackage()
    }
  }

  return (
    <>
      <MDBox lineHeight={1} display="flex" textAlign="left" gap={1}>
        {isPending ? (
          <Tooltip title={isAlamanaCompany() || isMylerzCompany() || isChahinCompany() ? "" : "Modifier"}>
            <MDButton
              disabled={isAlamanaCompany() || isMylerzCompany() || isChahinCompany()}
              size="small"
              variant="outlined"
              color={isAlamanaCompany() || isMylerzCompany() || isChahinCompany() ? "secondary" : "info"}
              onClick={updatePackage}
              iconOnly
            >
              <Icon>edit</Icon>
            </MDButton>
          </Tooltip>
        ) : (
          <Tooltip title="">
            <MDButton disabled size="small" variant="outlined" color="secondary" iconOnly>
              <Icon>edit</Icon>
            </MDButton>
          </Tooltip>
        )}
        {isLoadingPDF ? (
          <CircularProgress color="info" size={24} />
        ) : (
          <Tooltip title="Imprimer la facture">
            <MDButton size="small" variant="outlined" color="info" onClick={handlePrint} iconOnly>
              <Icon>print</Icon>
            </MDButton>
          </Tooltip>
        )}

        {isPending ? (
          <Tooltip title={isAlamanaCompany() || isChahinCompany() ? "" : "Supprimer"}>
            <MDButton
              disabled={isAlamanaCompany() || isChahinCompany()}
              size="small"
              variant="outlined"
              color="error"
              onClick={handleOpenDeleteModal}
              iconOnly
            >
              <Icon>delete</Icon>
            </MDButton>
          </Tooltip>
        ) : (
          <Tooltip title="">
            <MDButton disabled size="small" variant="outlined" color="error" iconOnly>
              <Icon>delete</Icon>
            </MDButton>
          </Tooltip>
        )}
      </MDBox>
      <Modal
        open={isDeleteModalOpened}
        onClose={handleCloseDeleteModal}
        aria-labelledby="confirm-remove_package-title"
        aria-describedby="confirm-remove_package-description"
      >
        <Card sx={style}>
          <MDTypography id="modal-modal-title" variant="h6" component="h2">
            Supprimer le colis
          </MDTypography>
          <MDTypography id="modal-modal-description" sx={{ mt: 2 }} variant="body2">
            Cliquez sur le bouton confirmer pour supprimer ce colis
          </MDTypography>
          <MDBox lineHeight={1} display="flex" justifyContent="flex-end" mt={2} gap={1}>
            <MDButton size="small" variant="outlined" color="secondary" onClick={handleCloseDeleteModal}>
              Annuler
            </MDButton>
            <MDButton size="small" variant="outlined" color="error" onClick={removePackage}>
              Confirmer
            </MDButton>
          </MDBox>
        </Card>
      </Modal>
      <MDSnackbar
        color={snackbarObject.color}
        icon={snackbarObject.icon}
        title="Colis"
        content={snackbarObject.content}
        open={snackbarObject.open}
        close={handleCloseSnackbar}
        dateTime=""
        sx={{
          float: "left",
        }}
      />
    </>
  )
}

// Get all package list from the server
const packageRows = (packageList) => {
  const localPackageList = []
  if (packageList.length === 0) return localPackageList
  for (let packageIndex = 0; packageIndex < packageList.length; packageIndex++) {
    const packageItem = packageList[packageIndex]

    localPackageList.push({
      package: (
        <MDTypography
          component="a"
          href={`colis/colis-details/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          #{packageItem.reference}
        </MDTypography>
      ),
      barcode: (
        <MDTypography
          component="a"
          href={`colis/colis-details/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {packageItem.barcode}
        </MDTypography>
      ),
      company: (
        <MDTypography
          component="a"
          href={`colis/colis-details/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {packageItem.companyName}
        </MDTypography>
      ),
      client: (
        <MDTypography
          component="a"
          href={`colis/colis-details/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {packageItem.clientName}
        </MDTypography>
      ),
      state: <StateCity state={packageItem.state} city={packageItem.city} />,
      clientPhoneNumber: (
        <MDTypography
          component="a"
          href={`colis/colis-details/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {packageItem.clientPhoneNumber}
        </MDTypography>
      ),
      price: (
        <MDTypography
          component="a"
          href={`colis/colis-details/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {packageItem.packagePrice}
        </MDTypography>
      ),
      status: (
        <MDBox ml={-1}>
          <MDBadge
            badgeContent={translateStatus(packageItem.status)}
            sx={{
              ".MuiBadge-badge": {
                background: generateColorPerStatus(packageItem.status),
              },
            }}
            variant="gradient"
            size="md"
          />
        </MDBox>
      ),
      isMissing: (
        <MDBox ml={-1}>
          <MDBadge
            badgeContent={packageItem.isMissing ? "oui" : "non"}
            color={packageItem.isMissing ? "success" : "error"}
            variant="gradient"
            size="md"
          />
        </MDBox>
      ),
      isPrinted: (
        <MDBox ml={-1}>
          <MDBadge
            badgeContent={packageItem.isPrinted ? "Imprimé" : "Non Imprimé"}
            color={packageItem.isPrinted ? "success" : "error"}
            variant="gradient"
            size="md"
          />
        </MDBox>
      ),
      date: (
        <MDTypography
          component="a"
          href={`colis/colis-details/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {moment(packageItem.createdAt).format("MMM DD, YYYY")}
        </MDTypography>
      ),
      actions: (
        <Action
          packageId={packageItem.packageId}
          companyName={packageItem.companyName}
          barcode={packageItem.barcode}
          isPending={packageItem.status === "pending"}
          packageDetails={packageItem}
        />
      ),
    })
  }
  return localPackageList
}

export default function packagesDataTable(packageList) {
  return {
    columns: [
      {
        id: "selection",
        Header: ({ getToggleAllRowsSelectedProps }) => <MDCheckbox {...getToggleAllRowsSelectedProps()} />,
        Cell: ({ row }) => <MDCheckbox {...row.getToggleRowSelectedProps()} />,
      },
      { Header: "colis (ref)", accessor: "package", width: "10%", align: "left" },
      { Header: "code à barre", accessor: "barcode", width: "10%", align: "left" },
      { Header: "entreprise", accessor: "company", align: "left" },
      { Header: "client(e)", accessor: "client", align: "left" },
      { Header: "état/ville", accessor: "state", align: "left" },
      { Header: "téléphone", accessor: "clientPhoneNumber", align: "center" },
      { Header: "prix (tnd)", accessor: "price", align: "center" },
      { Header: "statut", accessor: "status", align: "center" },
      { Header: "manquant", accessor: "isMissing", align: "center" },
      { Header: "impression", accessor: "isPrinted", align: "center" },
      { Header: "date", accessor: "date", align: "center" },
      { Header: "actions", accessor: "actions", align: "center" },
    ],
    rows: packageRows(packageList),
  }
}

// Setting default values for the props of Action
Action.defaultProps = {}

// Typechecking props of the Action
Action.propTypes = {
  packageId: PropTypes.number.isRequired,
}
